import React, { useEffect } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useEFiling } from '../contexts/EFilingContext';
import { buildAssignmentFillPatch, isCourtSourcedRoster } from '../lib/assignment-autofill';
import DocumentMetadataForm from './DocumentMetadataForm';

interface DocumentAssignmentSectionProps {
  brand: BrandConfig;
  /**
   * Render EVERY participant-class metadata item (caseParticipant +
   * caseAssignment — Refers-To, Party Served, Former/New Attorney, …), not
   * just the two assignment codes. Set by the confirm flow (WOL-419 / Walter
   * R69/R70): there the Upload step hides participant-class fields entirely,
   * so this section is their single home. Classic flow keeps the two-code
   * filter — the other fields live on Upload and would render twice.
   */
  includeAllPartyFields?: boolean;
}

/**
 * Per-document participant assignment on the About Your Case step (Walter
 * 2026-07-10 R20). Parties and attorneys are created HERE, but each document's
 * On Behalf Of (FILING_PARTY) and Filing Attorney (FILING_ATTORNEY) used to be
 * assignable only back on Upload Documents — the fee-gate walk-back one level
 * down: Upload (type docs) → forward here (add participants) → BACK to Upload
 * (assign per document) → forward again. This section renders those two
 * controls for every typed document right below Case Participants, so the
 * assignment completes on this step, strictly forward.
 *
 * Reuses DocumentMetadataForm filtered to the two assignment codes, so the
 * controls (including the R19 "Self-represented (no attorney)" option and the
 * WOL-344 string[] persistence) stay identical to Upload's — one writer path,
 * `updateDocumentMetadata`, shared with the Upload step. Auto-fill still runs
 * first (the unambiguous-attorney gap-fill in DocumentsTab), so in the common
 * single-attorney case these controls arrive already satisfied.
 */
const DocumentAssignmentSection: React.FC<DocumentAssignmentSectionProps> = ({
  brand,
  includeAllPartyFields = false,
}) => {
  const { wizardState, docTypeMetadata, updateDocumentMetadata } = useEFiling();
  const { documents, parties, attorneys } = wizardState;

  // R81/R82 (Walter): on a case-initiation there is no court roster, so the
  // extraction-minted entries Case Participants displays must be offerable in
  // these selectors (and eligible for the R58/R83 auto-assign). The R6
  // exclusion stays for subsequent filings, where the court roster is
  // authoritative.
  const offerExtractionEntries = wizardState.orderInfo.filingType !== 'subsequent-filing';

  // SPEC-SOR-220-v2 R6: the same data the offerable pool turns on decides the
  // roster's origin — the ONE predicate, not a flow-shaped second copy.
  const courtSourcedRoster = isCourtSourcedRoster({
    filingType: wizardState.orderInfo.filingType,
    parties,
  });

  // R87 (Walter, WOL-443): an existing-case filing with NO case attached has an
  // empty offerable roster BY DESIGN (the R6 rule refuses extraction entries) —
  // the way out is the case lookup, not manual re-entry. Point the empty state
  // there instead of the generic "Add them in Case Participants" dead-end.
  // Confirm flow only (includeAllPartyFields): the classic section sits on
  // About Your Case, a step after the lookup, and keeps its current copy.
  const emptyRosterHint =
    includeAllPartyFields &&
    wizardState.orderInfo.filingType === 'subsequent-filing' &&
    !wizardState.caseInfo.caseTrackingId
      ? 'None loaded yet — find your existing case in the Court section above; the court’s participants fill in from the case.'
      : undefined;

  // Gap-fill the assignments as participants land ON THIS STEP. The Upload
  // step's roster backstop can't cover this — DocumentsTab is unmounted while
  // the filer is here, so without this effect a party/attorney added on About
  // Your Case only auto-links after walking back to Upload: the exact
  // round-trip R20 forbids. Fill-only (never overwrites a selection); keyed on
  // the rosters, mirroring the DocumentsTab effect's dep reasoning.
  //
  // SPEC-SOR-220-v2 R5/R6: the fills route through the ONE guarded builder
  // (provenance + roster origin in, refusals respected), and what it fills is
  // written with the 'gapfill' kind so the slot records `prefilled` — a
  // prefill, never a filer choice.
  useEffect(() => {
    if (parties.length === 0) return;
    for (const doc of documents) {
      if (!doc.documentType?.code) continue;
      const patch = buildAssignmentFillPatch({
        metadataValues: doc.metadataValues || {},
        metadataItems: docTypeMetadata[doc.documentType.code] || [],
        parties,
        attorneys,
        offerExtractionEntries,
        assignmentProvenance: doc.assignmentProvenance,
        courtSourcedRoster,
      });
      if (Object.keys(patch).length > 0) {
        updateDocumentMetadata(doc.id, prev => ({ ...prev, ...patch }), 'gapfill');
      }
    }
  // documents deliberately excluded — updateDocumentMetadata mutates it and
  // would re-trigger; the roster deps re-run the fill when it matters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parties, attorneys, docTypeMetadata, offerExtractionEntries, courtSourcedRoster]);

  const rows = documents
    .filter(doc => doc.documentType?.code)
    .map(doc => ({
      doc,
      assignmentMeta: (docTypeMetadata[doc.documentType!.code] || []).filter(m =>
        includeAllPartyFields
          ? m.classType === 'caseParticipant' || m.classType === 'caseAssignment'
          : (m.code === 'FILING_PARTY' || m.code === 'FILING_ATTORNEY') &&
            m.valueRestriction !== 'new-data',
      ),
    }))
    .filter(({ assignmentMeta }) => assignmentMeta.length > 0);

  // Nothing to assign (no typed documents, or their policy metadata carries no
  // assignment fields) — render nothing rather than an empty header.
  if (rows.length === 0) return null;

  return (
    <div data-testid="document-assignment-section">
      <hr className="my-6" style={{ borderColor: brand.colors.border }} />
      <h2 className="text-lg font-semibold mb-1" style={{ color: brand.colors.text }}>
        Document Assignments
      </h2>
      <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
        Who each document is filed on behalf of, and by which attorney. Set here —
        no need to go back to Upload Documents.
      </p>
      {rows.map(({ doc, assignmentMeta }) => (
        <div
          key={doc.id}
          data-testid={`document-assignment-${doc.id}`}
          className="border rounded-lg p-4 mb-3"
          style={{ borderColor: brand.colors.border }}
        >
          <p className="text-sm font-medium mb-2" style={{ color: brand.colors.link }}>
            {doc.name}
            <span className="text-xs font-normal ml-2" style={{ color: brand.colors.textLight }}>
              {doc.documentType?.name}
            </span>
          </p>
          <DocumentMetadataForm
            metadata={assignmentMeta}
            parties={parties}
            attorneys={attorneys}
            offerExtractionEntries={offerExtractionEntries}
            emptyRosterHint={emptyRosterHint}
            values={doc.metadataValues || {}}
            onChange={(code, value) =>
              updateDocumentMetadata(doc.id, prev => ({ ...prev, [code]: value }))
            }
            brand={brand}
          />
        </div>
      ))}
    </div>
  );
};

export default DocumentAssignmentSection;
